import React, { useCallback } from 'react';
import { makeStyles, Theme, createStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import { useDropzone, FileRejection } from 'react-dropzone';
import { DocumentFile } from '../../../types/DocumentFile';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';

// ─── Documents dropzone ─────────────────────────────────────────────────────
// Ported from AttachmentsField.tsx (src/components/AttachmentsField.tsx - used by the email
// template dialogs' own attachment upload) - explicit request to match that component's visual
// design and drag-drop behaviour here instead of material-ui-dropzone's DropzoneArea, while
// staying wired to this drawer's own DocumentFile[] shape (a type shared across ~30 other
// files/mutations, see src/types/DocumentFile.tsx) rather than that component's own AttachedFile
// type, so nothing about DocumentFile itself changes. Unlike AttachmentsField, no file-type
// whitelist - explicit request to keep accepting any file type, matching this drawer's prior
// DropzoneArea behaviour (its own acceptedFiles prop was never set).
const MAX_DOCUMENT_FILES = 50;

const MAX_DOCUMENT_FILE_SIZE_BYTES = 15000000;

const useDocumentsDropzoneStyles = makeStyles((theme: Theme) =>
    createStyles({
        dropzone: {
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 10,
            padding: theme.spacing(4, 2),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1),
            cursor: 'pointer',
            transition: 'border-color 0.15s, background 0.15s',
            backgroundColor: theme.palette.grey[50],
            outline: 'none',
            userSelect: 'none',
        },
        dropzoneIdle: {
            '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: '#f0f7ff',
            },
        },
        dropzoneActive: {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#e3f2fd',
        },
        dropzoneRejected: {
            borderColor: theme.palette.error.main,
            backgroundColor: '#fff8f8',
        },
        dropzoneDisabled: {
            opacity: 0.45,
            cursor: 'not-allowed',
            pointerEvents: 'none',
        },
        dropzoneUploadIcon: {
            fontSize: 36,
            color: theme.palette.primary.light,
            marginBottom: theme.spacing(0.5),
        },
        dropzoneTitle: {
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.primary,
            textAlign: 'center',
        },
        dropzoneBrowse: {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
        },
        dropzoneSub: {
            fontSize: 11,
            color: theme.palette.text.disabled,
            textAlign: 'center',
            lineHeight: 1.6,
        },
        fileListHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
        },
        fileListTitle: {
            fontSize: 11,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
        fileCountBadge: {
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
        },
        fileCountBadgeWarn: {
            backgroundColor: '#fff3e0',
            color: '#e65100',
        },
        fileList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.75),
        },
        fileItem: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            padding: theme.spacing(1, 1.25),
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: '#fff',
            transition: 'border-color 0.12s',
        },
        fileIconBox: {
            width: 36,
            height: 36,
            borderRadius: 8,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
            '& .MuiSvgIcon-root': { fontSize: 20 },
        },
        fileMeta: {
            flex: 1,
            minWidth: 0,
        },
        fileName: {
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        fileDetail: {
            fontSize: 11,
            color: theme.palette.text.disabled,
            marginTop: 1,
        },
        fileLink: {
            color: theme.palette.primary.main,
            cursor: 'pointer',
            textDecoration: 'underline',
        },
        fileActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            flexShrink: 0,
        },
        fileRemoveBtn: {
            padding: 4,
            flexShrink: 0,
            color: theme.palette.text.disabled,
            '& .MuiSvgIcon-root': { fontSize: 16 },
            '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: '#ffebee',
            },
        },
    })
);

const formatDocumentFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageDocumentFile = (documentFile: DocumentFile): boolean => {
    if (documentFile.file?.type.startsWith('image/')) return true;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(documentFile.name);
};

const getDocumentFileIcon = (documentFile: DocumentFile) => {
    const fileType = documentFile.file?.type ?? '';

    if (isImageDocumentFile(documentFile)) return <ImageOutlinedIcon />;
    if (fileType === 'application/pdf' || /\.pdf$/i.test(documentFile.name)) return <PictureAsPdfOutlinedIcon />;
    if (fileType.includes('word') || /\.(doc|docx)$/i.test(documentFile.name)) return <DescriptionOutlinedIcon />;
    if (
        fileType.includes('excel') ||
        fileType.includes('spreadsheet') ||
        /\.(xls|xlsx|csv)$/i.test(documentFile.name)
    ) return <DescriptionOutlinedIcon />;
    return <InsertDriveFileOutlinedIcon />;
};

const getDocumentFileIconColor = (documentFile: DocumentFile): string => {
    const fileType = documentFile.file?.type ?? '';

    if (isImageDocumentFile(documentFile)) return '#7b61ff';
    if (fileType === 'application/pdf' || /\.pdf$/i.test(documentFile.name)) return '#e53935';
    if (fileType.includes('word') || /\.(doc|docx)$/i.test(documentFile.name)) return '#1565c0';
    if (
        fileType.includes('excel') ||
        fileType.includes('spreadsheet') ||
        /\.(xls|xlsx|csv)$/i.test(documentFile.name)
    ) return '#2e7d32';
    return '#546e7a';
};

interface DocumentsDropzoneProps {
    value: DocumentFile[];
    onChange: (files: DocumentFile[]) => void;
    onFileClicked: (file: DocumentFile) => void;
    generateUniqueFileName: (name: string, existing: DocumentFile[]) => string;
    onNotify: (message: string, severity: 'success' | 'error') => void;
}

export const DocumentsDropzone: React.FC<DocumentsDropzoneProps> = ({
    value, onChange, onFileClicked, generateUniqueFileName, onNotify,
}) => {
    const classes = useDocumentsDropzoneStyles();
    const documents = value ?? [];
    const visibleDocuments = documents.filter(documentFile => documentFile.status !== 2);
    const atLimit = visibleDocuments.length >= MAX_DOCUMENT_FILES;

    const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
        const remainingSlots = MAX_DOCUMENT_FILES - visibleDocuments.length;
        const existingFiles = [...documents];

        accepted.slice(0, remainingSlots).forEach(file => {
            const uniqueName = generateUniqueFileName(file.name, existingFiles);
            const renamed = uniqueName !== file.name ? new File([file], uniqueName, { type: file.type }) : file;
            existingFiles.push({ guidID: null, file: renamed, status: 1, name: uniqueName, url: '' });
        });

        // Real validation failures only (oversized) - "too many files" is never surfaced, same
        // as AttachmentsField.tsx: extras beyond the remaining slots are silently dropped.
        const sizeErrors = rejected.filter(rejection => rejection.errors.some(error => error.code === 'file-too-large'));
        if (sizeErrors.length > 0) {
            const rejectedNames = sizeErrors.map(rejection => rejection.file.name).join(', ');
            onNotify(`${rejectedNames} exceeded the 15 MB limit and could not be added.`, 'error');
        }

        if (accepted.length > 0) {
            onChange(existingFiles);
            onNotify(`File ${existingFiles[existingFiles.length - 1].name} successfully added`, 'success');
        }
    }, [documents, visibleDocuments.length, generateUniqueFileName, onChange, onNotify]);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        maxSize: MAX_DOCUMENT_FILE_SIZE_BYTES,
        disabled: atLimit,
    });

    const removeDocument = (target: DocumentFile) => {
        if (target.guidID) {
            onChange(documents.map(documentFile => (documentFile === target ? { ...documentFile, status: 2 } : documentFile)));
            return;
        }
        onChange(documents.filter(documentFile => documentFile !== target));
    };

    const dropzoneClassName = [
        classes.dropzone,
        isDragReject ? classes.dropzoneRejected : '',
        isDragActive && !isDragReject ? classes.dropzoneActive : '',
        !isDragActive && !atLimit ? classes.dropzoneIdle : '',
        atLimit ? classes.dropzoneDisabled : '',
    ].filter(Boolean).join(' ');

    return (
        <div>
            <div {...getRootProps({ className: dropzoneClassName })}>
                <input {...getInputProps()} />

                <CloudUploadOutlinedIcon className={classes.dropzoneUploadIcon} />

                <Typography className={classes.dropzoneTitle}>
                    {isDragActive && !isDragReject && 'Drop files here...'}
                    {isDragReject && 'Some files will be rejected'}
                    {!isDragActive && !atLimit && (
                        <>Drag &amp; drop files, or <span className={classes.dropzoneBrowse}>browse</span></>
                    )}
                    {atLimit && `Maximum of ${MAX_DOCUMENT_FILES} files reached`}
                </Typography>

                <Typography className={classes.dropzoneSub}>
                    {/* MAX_DOCUMENT_FILE_SIZE_BYTES is 15000000 - a round 15MB only under the
                        decimal (1000*1000) definition, not the binary one (1024*1024, which
                        formatDocumentFileSize below correctly uses for actual file sizes) -
                        this is just the limit's own display, so divide accordingly to show a
                        clean "15", not "14.3051...". */}
                    Up to {MAX_DOCUMENT_FILES} files · max {MAX_DOCUMENT_FILE_SIZE_BYTES / (1000 * 1000)} MB each
                </Typography>
            </div>

            {visibleDocuments.length > 0 && (
                <>
                    <div className={classes.fileListHeader}>
                        <Typography className={classes.fileListTitle}>
                            {visibleDocuments.length} file{visibleDocuments.length !== 1 ? 's' : ''}
                        </Typography>
                        <span className={[classes.fileCountBadge, atLimit ? classes.fileCountBadgeWarn : ''].join(' ')}>
                            {visibleDocuments.length} / {MAX_DOCUMENT_FILES}
                        </span>
                    </div>

                    <div className={classes.fileList}>
                        {visibleDocuments.map((documentFile, index) => (
                            <div key={`${documentFile.guidID ?? documentFile.name}-${index}`} className={classes.fileItem}>
                                <div className={classes.fileIconBox} style={{ color: getDocumentFileIconColor(documentFile) }}>
                                    {getDocumentFileIcon(documentFile)}
                                </div>

                                <div className={classes.fileMeta}>
                                    {documentFile.url ? (
                                        <Typography
                                            className={[classes.fileName, classes.fileLink].join(' ')}
                                            title={documentFile.name}
                                            onClick={() => onFileClicked(documentFile)}
                                        >
                                            {documentFile.name}
                                        </Typography>
                                    ) : (
                                        <Typography className={classes.fileName} title={documentFile.name}>
                                            {documentFile.name}
                                        </Typography>
                                    )}
                                    <Typography className={classes.fileDetail}>
                                        {/* A saved/fetched document is hydrated with a placeholder empty File
                                            (see enquiryDetail.files.map's `file: f.file || new File([], f.name)`
                                            above) so DropzoneArea's own initialFiles prop had something to
                                            render - that placeholder's size is always 0, so guidID (only ever
                                            set on a file that's actually been saved) is the real signal here,
                                            not documentFile.file's mere presence. */}
                                        {documentFile.guidID ? 'Saved attachment' : formatDocumentFileSize(documentFile.file?.size ?? 0)}
                                    </Typography>
                                </div>

                                <div className={classes.fileActions}>
                                    {documentFile.url && (
                                        <Tooltip title="Download" placement="top">
                                            <IconButton
                                                className={classes.fileRemoveBtn}
                                                size="small"
                                                onClick={() => onFileClicked(documentFile)}
                                            >
                                                <GetAppOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    <Tooltip title="Remove" placement="top">
                                        <IconButton
                                            className={classes.fileRemoveBtn}
                                            size="small"
                                            onClick={() => removeDocument(documentFile)}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
